<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import ClientOnly from '@/components/SSR/ClientOnly.vue'
  import { useDebounceFn } from '@vueuse/core'
  import SearchModal from '@/components/Modals/SearchModal/SearchModal.vue'
  import SearchInput from '@/components/Search/SearchInput.vue'
  import CartButton from './redesign/CartButton.vue'
  import UserAccountButton from '@/components/Buttons/UserAccountButton/UserAccountButtonRedesign.vue'
  import MenuTooltip from '@/components/Menu/MenuTooltip.vue'
  import Button from '@/components/partials/Buttons/Button/Button.vue'
  import Tooltip from '@/components/partials/Tooltip.vue'
  import LanguageSwitcherCircle from '@/pages/home/components/Navigation/LanguageSwitcherCircle.vue'
  import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'
  import Icon from '@/components/partials/Icon.vue'
  import { storeToRefs } from 'pinia'
  import { usePageConfigurationStore } from '@/stores/ssr/usePageConfiguration'

  const route = useRoute()
  const { t } = useI18n()

  const { showLogo } = storeToRefs(usePageConfigurationStore())
  const marketplaceStore = useMarketplaceStore()
  const { searchQuery } = storeToRefs(marketplaceStore)

  const props = defineProps<{
    logoLink: object | string
  }>()

  const loading = ref<boolean>(true)

  const state = reactive({
    searchModalOpen: false,
  })

  const hasSearch = computed(() => searchQuery.value !== null)
  const showSearch = computed(() => route.name.endsWith('marketplace'))

  const updateSearchQuery = useDebounceFn(async (searchQuery) => {
    marketplaceStore.setSearchQuery(searchQuery === '' ? null : searchQuery)
  }, 500)

  const showCartButton = computed(
    () =>
      !route.name.includes('thanks') &&
      !route.name.includes('checkout') &&
      !route.name.includes('establishment'),
  )

  const showMenu = computed(() => !route.name.includes('checkout'))

  const fluid = route.name.endsWith('marketplace') || route.name.endsWith('slug')

  const router = useRouter()

  async function openLink(event) {
    event.preventDefault()

    if (typeof props.logoLink === 'string') {
      window.location.href = props.logoLink
    } else {
      await router.push(props.logoLink)
    }
  }

  onMounted(() => {
    loading.value = false
  })
</script>

<template>
  <nav
    class="container nav nav--pidgey__b"
    :class="{ 'container--fluid': fluid }"
    data-test-id="navbar-desktop"
  >
    <SearchModal :open="state.searchModalOpen" @close="state.searchModalOpen = false" />

    <a @click.prevent="openLink" class="nav__logo-link" href="#" data-test-id="navbar-logo">
      <img
        v-if="showLogo && !loading"
        class="nav__logo"
        alt="Bistroo"
        src="/assets/img/brand/bistroo-logo.svg"
        width="126"
        data-not-lazy
      />
    </a>

    <ul class="nav__menu">
      <li v-if="showSearch" class="nav__item">
        <SearchInput
          :model-value="searchQuery"
          @change="updateSearchQuery"
          fluid
          v-if="hasSearch"
        />
        <Button
          class="search"
          type="secondary"
          @click="state.searchModalOpen = true"
          data-test-id="search-button"
          v-else
        >
          <template #icon>
            <Icon name="search-line" />
          </template>
          {{ t('terms.search') }}
        </Button>
      </li>
      <li v-if="showCartButton" class="nav__item">
        <ClientOnly>
          <CartButton data-test-id="cart-button" />
        </ClientOnly>
      </li>
      <li>
        <ClientOnly>
          <UserAccountButton />
        </ClientOnly>
      </li>
      <li>
        <LanguageSwitcherCircle class="desktop-language-selector" />
      </li>
      <li v-if="showMenu" class="nav__item">
        <Tooltip ref="tooltip">
          <template #default="{ active }">
            <Button type="secondary" data-test-id="desktop-menu-tooltip">
              <template #icon>
                <Icon name="cross-line" v-if="active" />
                <Icon name="menu-line" v-else />
              </template>
            </Button>
          </template>
          <template #content>
            <MenuTooltip @close-tooltip="() => $refs.tooltip.close()" />
          </template>
        </Tooltip>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
  @import '@/assets/css/mixins/breakpoints-up.scss';

  .nav {
    $self: &;
    display: none;
    justify-content: space-between;

    @include lg-up {
      display: flex;
    }

    &__logo-link {
      display: flex;
      align-items: center;

      &:deep(.b-icon-icon svg) {
        width: 2.5rem;
        height: 2.5rem;
        margin-top: 0.25rem;
      }
    }

    &__logo {
      cursor: pointer;
    }

    &__menu {
      display: flex;
      margin: 0 -0.75rem;
      padding: 0;

      li {
        align-content: center;
      }
    }

    &__item {
      display: flex;
      justify-content: center;
      margin: 0 0.75rem;
      vertical-align: middle;
    }

    &:deep(.tooltip),
    &:deep(.tooltip__element) {
      display: flex;
      justify-content: center;
    }

    &--pidgey__b {
      &:deep(.button .b-icon-icon),
      &:deep(.button .b-icon-icon svg) {
        width: 1.5rem;
        height: 1.5rem;
      }

      #{$self}__menu {
        gap: 0.75rem;
      }

      #{$self}__item {
        margin: 0;
      }
    }
  }

  :deep(.desktop-language-selector) {
    &.button::before {
      background: linear-gradient(66deg, rgb(255, 42, 161) 0%, rgb(255, 157, 5) 100%);
    }

    .button__content {
      background: #ffffff;
      padding: 0.75rem;
      border: 1px solid var(--color-neutral-quaternary);

      .flag {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }
</style>
